import React, { useState, useEffect } from "react";
import { Select, Empty, Skeleton, Tabs, Card, Tag, Button, Form, DatePicker, Input, notification, message } from "antd";
import { CalendarOutlined, CheckCircleOutlined, CloseCircleOutlined, StopOutlined } from "@ant-design/icons";
import { FaBuilding, FaCalendarTimes, FaQuestionCircle } from "react-icons/fa";
import { FaClock } from "react-icons/fa6";
import PageTitle from "../../Ui/PageTitle";
import WorkingDayCard from "./WorkingDayCard";
import { useClinics } from "../../../hooks/useClinics";
import drEasyApi from "../../../Api/Customapi";
import TabPane from "antd/es/tabs/TabPane";

const WorkingDays = () => {
    const [selectedClinic, setSelectedClinic] = useState("all");
    const [workingDays, setWorkingDays] = useState([]);
    const [vacations, setVacations] = useState([]);
    const [cancelingId, setCancelingId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();

    const token = localStorage.getItem('docTkn') || sessionStorage.getItem('docTkn');

    const { clinicsQuery } = useClinics({ pageNumber: 1, pageSize: 100 });

    const dayMap = {
        0: "Sunday", 1: "Monday", 2: "Tuesday", 3: "Wednesday",
        4: "Thursday", 5: "Friday", 6: "Saturday",
    };

    // ─── Vacations ───────────────────────────────────────────────────────────────
    const fetchVacations = async () => {
        try {
            const res = await drEasyApi.get("/api/Vacation/GetMyVacations", {
                headers: { Authorization: `Bearer ${token}` },
            });
            setVacations(res.data);
        } catch (err) {
            if (err.response?.status === 403) message.error(err.response.data[0]);
        }
    };

    useEffect(() => {
        if (token) fetchVacations();
    }, [token]);

    const onFinish = async (values) => {
        try {
            setLoading(true);
            await drEasyApi.post("/api/Vacation/Create", {
                startDate: values.startDate.toISOString(),
                endDate: values.endDate.toISOString(),
                reason: values.reason,
            }, { headers: { Authorization: `Bearer ${token}` } });
            notification.success({ message: "Vacation Created", description: "Your vacation was successfully saved." });
            form.resetFields();
            fetchVacations();
        } catch (err) {
            const apiMessage = Array.isArray(err.response?.data)
                ? err.response.data[0]
                : err.response?.data?.message || "Something went wrong.";
            notification.warning({ message: "Warning", description: apiMessage });
        } finally {
            setLoading(false);
        }
    };

    const cancelVacation = async (id) => {
        try {
            setCancelingId(id);
            await drEasyApi.put("/api/Vacation/Cancel", { vacationId: id }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            notification.success({ message: "Cancelled", description: "Vacation was cancelled successfully." });
            fetchVacations();
        } catch (err) {
            notification.error({
                message: "Cancel Failed",
                description: err.response?.data?.message || "Could not cancel vacation",
            });
        } finally {
            setCancelingId(null);
        }
    };

    // ─── Working Days ────────────────────────────────────────────────────────────
    useEffect(() => {
        if (clinicsQuery.data?.clinics) {
            const allWorkingDays = clinicsQuery.data.clinics.flatMap((clinic) =>
                clinic.workingDays.map((day) => ({
                    ...day,
                    clinicName: clinic.name,
                    clinicId: clinic.id,
                }))
            );
            setWorkingDays(allWorkingDays);
        }
    }, [clinicsQuery.data]);

    const filteredWorkingDays =
        selectedClinic === "all"
            ? workingDays
            : workingDays.filter((day) => day.clinicId === selectedClinic);

    const groupedWorkingDays = (clinicsQuery.data?.clinics || []).reduce((acc, clinic) => {
        const clinicDays = filteredWorkingDays.filter((day) => day.clinicId === clinic.id);
        if (clinicDays.length > 0) {
            acc[clinic.id] = {
                clinicName: clinic.name,
                days: clinicDays,
                isOwned: clinic.isOwned,
            };
        }
        return acc;
    }, {});

    // ─── Loading / Error ─────────────────────────────────────────────────────────
    if (clinicsQuery.isLoading) {
        return (
            <div className="animate-fade-in">
                <PageTitle title="Working Days" subtitle="Loading..." />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[...Array(6)].map((_, i) => <Skeleton key={i} active paragraph={{ rows: 4 }} />)}
                </div>
            </div>
        );
    }

    if (clinicsQuery.isError) {
        return (
            <div className="animate-fade-in">
                <PageTitle title="Working Days" subtitle="Error loading data" />
                <div className="bg-red-50 border-l-4 border-red-500 p-4">
                    <p className="text-sm text-red-700">{clinicsQuery.error.message}</p>
                </div>
            </div>
        );
    }

    // ─── Render ──────────────────────────────────────────────────────────────────
    return (
        <div className="animate-fade-in">
            <Tabs>
                {/* Working Days — read only */}
                <TabPane tab="Working Days" key="working">
                    <PageTitle
                        title="Working Days"
                        subtitle="Working days configured for each clinic"
                    />

                    {/* Clinic filter */}
                    <div className="mb-6">
                        <div className="flex items-center sm:justify-start justify-center">
                            <FaBuilding className="mr-2 text-primary sm:text-lg text-2xl" />
                            <span className="mr-3 text-gray-700 sm:inline-block hidden">Filter by clinic:</span>
                            <Select
                                value={selectedClinic}
                                onChange={setSelectedClinic}
                                className="w-60"
                                placeholder="Select a clinic"
                                loading={clinicsQuery.isLoading}
                            >
                                <Select.Option value="all">All Clinics</Select.Option>
                                {clinicsQuery.data?.clinics?.map((clinic) => (
                                    <Select.Option key={clinic.id} value={clinic.id}>
                                        {clinic.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </div>
                    </div>

                    {Object.keys(groupedWorkingDays).length === 0 ? (
                        <Empty
                            image={<CalendarOutlined style={{ fontSize: 48, color: "#bfbfbf" }} />}
                            description={
                                selectedClinic === "all"
                                    ? "No working days configured for any clinic"
                                    : "No working days configured for this clinic"
                            }
                        />
                    ) : (
                        <div className="space-y-6">
                            {Object.values(groupedWorkingDays).map((group) => (
                                <WorkingDayCard
                                    key={group.clinicName}
                                    group={group}
                                    dayMap={dayMap}
                                />
                            ))}
                        </div>
                    )}
                </TabPane>

                {/* Vacations — create + cancel kept */}
                <TabPane tab="Vacations" key="Vacations" className="px-5">
                    <Form onFinish={onFinish} layout="vertical" form={form}>
                        <div className="flex gap-5">
                            <Form.Item
                                label="Start Date"
                                name="startDate"
                                rules={[{ required: true, message: "Please select a start date" }]}
                            >
                                <DatePicker />
                            </Form.Item>
                            <Form.Item
                                label="End Date"
                                name="endDate"
                                rules={[{ required: true, message: "Please select an end date" }]}
                            >
                                <DatePicker />
                            </Form.Item>
                        </div>
                        <Form.Item
                            label="Reason"
                            name="reason"
                            rules={[{ required: true, message: "Please enter a reason" }]}
                        >
                            <Input.TextArea rows={3} className="w-[23%]" />
                        </Form.Item>
                        <Button className="bg-primary text-white" htmlType="submit" loading={loading}>
                            + Create Vacation
                        </Button>
                    </Form>

                    <hr className="my-5" />

                    <div className="mt-6">
                        <h3 className="text-lg font-semibold mb-4">My Vacations</h3>
                        {vacations.length === 0 ? (
                            <Empty description="No vacations found" />
                        ) : (
                            <div className="grid md:grid-cols-3 gap-4">
                                {vacations.map((vac) => (
                                    <Card
                                        key={vac.id}
                                        title={
                                            <div className="flex items-center gap-2">
                                                <FaCalendarTimes className="text-primary" /> Vacation Period
                                            </div>
                                        }
                                        extra={
                                            !vac.isCancelled && (
                                                <Button
                                                    type="default"
                                                    danger
                                                    size="small"
                                                    onClick={() => cancelVacation(vac.id)}
                                                    loading={cancelingId === vac.id}
                                                    icon={<CloseCircleOutlined />}
                                                >
                                                    Cancel
                                                </Button>
                                            )
                                        }
                                        className="shadow-sm border"
                                    >
                                        <p><FaClock className="inline mr-2 text-primary" /><strong>From:</strong> {new Date(vac.startDate).toLocaleDateString()}</p>
                                        <p className="py-2"><FaClock className="inline mr-2 text-primary" /><strong>To:</strong> {new Date(vac.endDate).toLocaleDateString()}</p>
                                        <p className="flex items-center gap-3 pb-2">
                                            <FaQuestionCircle className="text-red-700" />
                                            <strong>Reason:</strong> {vac.reason || ""}
                                        </p>
                                        <Tag
                                            color={vac.isCancelled ? "red" : "green"}
                                            icon={vac.isCancelled ? <StopOutlined /> : <CheckCircleOutlined />}
                                        >
                                            {vac.isCancelled ? "Cancelled" : "Active"}
                                        </Tag>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </div>
                </TabPane>
            </Tabs>
        </div>
    );
};

export default WorkingDays;